import {Ability, ConditionType} from '../../../../generated/types';
import {conditionLabel} from './labels';

export type RollKind = 'check' | 'save' | 'attack' | 'initiative' | 'deathSave';

export type RollMode = 'normal' | 'advantage' | 'disadvantage';

export type RollContext = {
	kind: RollKind;
	/** характеристика проверки или испытания */
	ability?: Ability;
	/** навык проверки */
	skill?: string;
};

type ConditionRule = {
	advantage?: RollKind[];
	disadvantage?: RollKind[];
	/** испытания этих характеристик проваливаются автоматически */
	autoFailSaves?: Ability[];
	disadvantageSaves?: Ability[];
	zeroSpeed?: boolean;
	/** что ещё делает состояние, для подсказки на листе */
	note?: string;
	includes?: ConditionType[];
};

const PHYSICAL_SAVES = [Ability.Strength, Ability.Dexterity];

const CONDITION_RULES: Partial<Record<ConditionType, ConditionRule>> = {
	[ConditionType.Blinded]: {disadvantage: ['attack'], note: 'проверки, требующие зрения, проваливаются'},
	[ConditionType.Charmed]: {note: 'нельзя атаковать очаровавшего'},
	[ConditionType.Deafened]: {note: 'проверки, требующие слуха, проваливаются'},
	[ConditionType.Frightened]: {disadvantage: ['check', 'attack'], note: 'пока источник страха в поле зрения'},
	[ConditionType.Grappled]: {
		disadvantage: ['attack'],
		zeroSpeed: true,
		note: 'помеха к атакам по всем, кроме схватившего',
	},
	[ConditionType.Incapacitated]: {
		disadvantage: ['initiative'],
		note: 'нельзя совершать действия, бонусные и ответные действия',
	},
	[ConditionType.Invisible]: {advantage: ['initiative', 'attack']},
	[ConditionType.Paralyzed]: {
		autoFailSaves: PHYSICAL_SAVES,
		zeroSpeed: true,
		includes: [ConditionType.Incapacitated],
	},
	[ConditionType.Petrified]: {
		autoFailSaves: PHYSICAL_SAVES,
		zeroSpeed: true,
		note: 'сопротивление всему урону',
		includes: [ConditionType.Incapacitated],
	},
	[ConditionType.Poisoned]: {disadvantage: ['check', 'attack']},
	[ConditionType.Prone]: {disadvantage: ['attack']},
	[ConditionType.Restrained]: {
		disadvantage: ['attack'],
		disadvantageSaves: [Ability.Dexterity],
		zeroSpeed: true,
	},
	[ConditionType.Stunned]: {autoFailSaves: PHYSICAL_SAVES, includes: [ConditionType.Incapacitated]},
	[ConditionType.Unconscious]: {
		autoFailSaves: PHYSICAL_SAVES,
		zeroSpeed: true,
		includes: [ConditionType.Incapacitated, ConditionType.Prone],
	},
};

const matchesKind = (kinds: RollKind[] | undefined, kind: RollKind) =>
	!!kinds?.some((ruleKind) => ruleKind === kind || (ruleKind === 'check' && kind === 'initiative'));

export function activeConditions(conditions: string[]): ConditionType[] {
	const active = new Set<ConditionType>();
	const add = (condition: ConditionType) => {
		if (active.has(condition)) return;
		active.add(condition);
		CONDITION_RULES[condition]?.includes?.forEach(add);
	};
	conditions.forEach((condition) => add(condition as ConditionType));
	return [...active];
}

export type RollAdvice = {
	mode: RollMode;
	/** состояния, из-за которых выбран режим */
	/** что дало преимущество или помеху: состояния и действующие эффекты */
	reasons: string[];
	autoFail: string[];
};

export type ModeSource = {
	title: string;
	mode: 'advantage' | 'disadvantage';
};

export function rollAdvice(conditions: string[], {kind, ability}: RollContext, extra: ModeSource[] = []): RollAdvice {
	const advantage: string[] = [];
	const disadvantage: string[] = [];
	const autoFail: string[] = [];

	for (const condition of activeConditions(conditions)) {
		const rule = CONDITION_RULES[condition];
		if (!rule) continue;
		const title = conditionLabel(condition);
		if (matchesKind(rule.advantage, kind)) advantage.push(title);
		if (matchesKind(rule.disadvantage, kind)) disadvantage.push(title);
		if (kind === 'save' && ability && rule.disadvantageSaves?.includes(ability)) disadvantage.push(title);
		if (kind === 'save' && ability && rule.autoFailSaves?.includes(ability)) autoFail.push(title);
	}
	for (const source of extra) (source.mode === 'advantage' ? advantage : disadvantage).push(source.title);

	if (advantage.length && !disadvantage.length) return {mode: 'advantage', reasons: advantage, autoFail};
	if (disadvantage.length && !advantage.length) return {mode: 'disadvantage', reasons: disadvantage, autoFail};
	return {mode: 'normal', reasons: [...advantage, ...disadvantage], autoFail};
}

export const hasZeroSpeed = (conditions: string[]) =>
	activeConditions(conditions).some((condition) => CONDITION_RULES[condition]?.zeroSpeed);

const KIND_LABELS: Record<RollKind, string> = {
	check: 'проверкам',
	save: 'испытаниям',
	attack: 'броскам атаки',
	initiative: 'инициативе',
	deathSave: 'испытаниям против смерти',
};

export function conditionSummary(condition: ConditionType): string[] {
	const rule = CONDITION_RULES[condition];
	if (!rule) return [];
	const kinds = (list: RollKind[] | undefined) => (list || []).map((kind) => KIND_LABELS[kind]).join(' и ');
	return [
		rule.advantage && `преимущество к ${kinds(rule.advantage)}`,
		rule.disadvantage && `помеха к ${kinds(rule.disadvantage)}`,
		rule.disadvantageSaves && 'помеха к испытаниям Ловкости',
		rule.autoFailSaves && 'автоматический провал испытаний Силы и Ловкости',
		rule.zeroSpeed && 'скорость 0',
		rule.note,
	].filter((text): text is string => !!text);
}
